import { Zap } from 'lucide-react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { ZapDialog } from '@/components/ZapDialog';
import { Button } from '@/components/ui/button';
import type { Event } from 'nostr-tools';

const CREATOR_PUBKEY = 'df478568479de26b4a83c1bdc4dbab61b5cc82e1a312e2b28bc815a12a951e67';

/**
 * A zap button targeting the CashuCards creator (npub1marc26z...).
 * Fetches their kind 0 profile event and uses it as the ZapDialog target.
 */
export function ZapCreator() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  const { data: profileEvent } = useQuery({
    queryKey: ['nostr', 'profile-event', CREATOR_PUBKEY],
    queryFn: async () => {
      const events = await nostr.query(
        [{ kinds: [0], authors: [CREATOR_PUBKEY], limit: 1 }],
        { signal: AbortSignal.timeout(5000) },
      );
      return (events[0] as unknown as Event) ?? null;
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  // Don't render if no profile event found, or user is the creator themselves
  if (!profileEvent || (user && user.pubkey === CREATOR_PUBKEY)) {
    return null;
  }

  return (
    <ZapDialog target={profileEvent}>
      <Button
        variant="outline"
        size="sm"
        className="gap-2 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:border-amber-400 dark:hover:border-amber-600 transition-all"
      >
        <Zap className="w-4 h-4" />
        Zap the Creator
      </Button>
    </ZapDialog>
  );
}
